import { yupResolver } from '@hookform/resolvers/yup';
import { scrollToFirstErrorInForm } from '@openx/components-core/lib/utils/scrollToFirstErrorInForm.ts';
import { onKeyDown } from '@openx/components-form/onKeyDown.ts';
import type { LalSegmentFormState } from '@openx/types/segments.ts';
import { type JSX, useEffect } from 'react';
import { FormProvider, type SubmitErrorHandler, type UseFormSetError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LalSegmentsFormSections } from './sections/LalSegmentsFormSections.tsx';
import { getLalSegmentsFormDefaultValues } from './utils/getLalSegmentsFormDefaultValues.ts';
import { getValidationSchema } from './validationSchema.ts';

export type LalSegmentsFormProps = {
  goBack: () => void;
  initialValues: Partial<LalSegmentFormState>;
  onSubmit: (formValues: LalSegmentFormState, setError: UseFormSetError<LalSegmentFormState>) => void;
  isSubmitting: boolean;
  setSelectedAccountId?: (value: string) => void;
  isEdit?: boolean;
};

export const LalSegmentsForm = ({
  goBack,
  initialValues,
  onSubmit,
  isSubmitting,
  setSelectedAccountId,
  isEdit = false,
}: LalSegmentsFormProps): JSX.Element => {
  const { t } = useTranslation();

  const defaultValues = getLalSegmentsFormDefaultValues(initialValues, t);

  const formMethods = useForm<LalSegmentFormState>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const onSubmitForm = (values: LalSegmentFormState): void => {
    onSubmit(values, formMethods.setError);
  };

  const onInvalidSubmit: SubmitErrorHandler<LalSegmentFormState> = errors => {
    const formElement = document.querySelector('[data-test="lal-segments-form"]');

    scrollToFirstErrorInForm(errors, {
      root: formElement ?? document,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount to validate prefilled seeds
  useEffect(() => {
    if (!isEdit && initialValues?.seeds && initialValues?.seeds?.length > 0) {
      formMethods.trigger();
    }
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        data-test="lal-segments-form"
        autoComplete="off"
        noValidate
        onSubmit={formMethods.handleSubmit(onSubmitForm, onInvalidSubmit)}
        onKeyDown={onKeyDown}
      >
        <LalSegmentsFormSections
          goBack={goBack}
          isSubmitting={isSubmitting}
          setSelectedAccountId={setSelectedAccountId}
          isEdit={isEdit}
        />
      </form>
    </FormProvider>
  );
};
